import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLogo from './components/CompanyLogo';
import LoginForm from './components/LoginForm';
import RoleIndicator from './components/RoleIndicator';
import LoadingOverlay from './components/LoadingOverlay';
import ErrorAlert from './components/ErrorAlert';

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authenticatedRole, setAuthenticatedRole] = useState(null);
  const [showRoleIndicator, setShowRoleIndicator] = useState(false);

  // Mock user credentials for different roles
  const mockCredentials = {
    'admin@everforce.com': { password: 'admin123', role: 'admin', name: 'Administrador Sistema' },
    'ventas@everforce.com': { password: 'ventas123', role: 'sales', name: 'María González' },
    'almacen@everforce.com': { password: 'almacen123', role: 'warehouse', name: 'Carlos Ruiz' },
    'logistica@everforce.com': { password: 'logistica123', role: 'logistics', name: 'Ana Martín' },
    'contabilidad@everforce.com': { password: 'contabilidad123', role: 'accounting', name: 'Luis Fernández' },
    'gerencia@everforce.com': { password: 'gerencia123', role: 'management', name: 'Isabel Torres' }
  };

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('everforce_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData.token && userData.role) {
          navigate('/dashboard');
        }
      } catch (error) {
        localStorage.removeItem('everforce_user');
      }
    }
  }, [navigate]);

  const simulateNetworkDelay = () => {
    return new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));
  };

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate network delay
      await simulateNetworkDelay();

      // Validate credentials
      const userCredentials = mockCredentials[formData.email.toLowerCase()];
      
      if (!userCredentials) {
        throw new Error('invalid_credentials');
      }

      if (userCredentials.password !== formData.password) {
        throw new Error('invalid_credentials');
      }

      // Simulate successful authentication
      const userData = {
        email: formData.email,
        name: userCredentials.name,
        role: userCredentials.role,
        token: `mock_token_${Date.now()}`,
        loginTime: new Date().toISOString(),
        rememberMe: formData.rememberMe
      };

      // Save user data
      if (formData.rememberMe) {
        localStorage.setItem('everforce_user', JSON.stringify(userData));
      } else {
        sessionStorage.setItem('everforce_user', JSON.stringify(userData));
      }

      // Show role indicator briefly before redirect
      setAuthenticatedRole(userCredentials.role);
      setShowRoleIndicator(true);

      // Redirect to dashboard after showing role
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      setIsLoading(false);
      
      // Handle different error types
      if (error.message === 'invalid_credentials') {
        setError({
          type: 'invalid_credentials',
          message: 'Credenciales incorrectas'
        });
      } else if (error.name === 'NetworkError') {
        setError({
          type: 'network_error',
          message: 'Error de conexión'
        });
      } else {
        setError({
          type: 'general',
          message: 'Error inesperado durante el inicio de sesión'
        });
      }
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  const handleSessionTimeout = () => {
    setError({
      type: 'session_expired',
      message: 'Su sesión ha expirado'
    });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="relative bg-surface rounded-lg shadow-card border border-border p-8">
          {/* Loading Overlay */}
          <LoadingOverlay 
            isVisible={isLoading} 
            message={showRoleIndicator ? 'Configurando acceso...' : 'Verificando credenciales...'}
          />

          {/* Company Logo */}
          <CompanyLogo />

          {/* Error Alert */}
          <ErrorAlert error={error} onDismiss={handleErrorDismiss} />

          {/* Login Form */}
          <LoginForm onSubmit={handleLogin} isLoading={isLoading} />

          {/* Role Indicator */}
          <RoleIndicator 
            userRole={authenticatedRole} 
            isVisible={showRoleIndicator && !isLoading} 
          />

          {/* Footer Info */}
          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-center">
              <p className="text-xs text-text-muted">
                Sistema de Gestión Empresarial Everforce
              </p>
              <p className="text-xs text-text-muted mt-1">
                © {new Date().getFullYear()} Everforce. Todos los derechos reservados.
              </p>
            </div>
            
            {/* Demo Credentials Hint */}
            <div className="mt-4 p-3 bg-secondary-50 rounded-md">
              <p className="text-xs text-text-secondary text-center">
                <strong>Credenciales de prueba:</strong><br />
                admin@everforce.com / admin123<br />
                ventas@everforce.com / ventas123
              </p>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="text-center">
          <p className="text-xs text-text-muted">
            Acceso seguro con cifrado SSL. Sus datos están protegidos.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;